import { useEffect } from 'react';

/**
 * Head/meta management for a client-rendered SPA (no SSR helmet pipeline).
 * Googlebot renders JavaScript, so tags set here are what gets indexed,
 * the important part is that every route sets a distinct title, description,
 * and canonical URL, and that private routes carry noindex.
 */

export const SITE_ORIGIN = 'https://orthodoxway.co';
const TITLE_SUFFIX = ' • Orthodox Way';

export interface MetaInput {
  /** Page title WITHOUT the site suffix. */
  title: string;
  description: string;
  /** Absolute URL or site-relative path for og:image. */
  ogImage?: string;
  /** Canonical path; defaults to the current location.pathname. */
  path?: string;
  /** Ask crawlers not to index (login, profile, admin, …). */
  noindex?: boolean;
}

/** Plain-text excerpt for meta descriptions: strips tags, collapses whitespace, cuts at a word boundary. */
export function excerpt(text: string | null | undefined, fallback: string, max = 160): string {
  const plain = (text ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!plain) return fallback;
  if (plain.length <= max) return plain;
  const cut = plain.slice(0, max);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(' '), 1))}…`;
}

function absolute(url: string): string {
  return url.startsWith('http') ? url : `${SITE_ORIGIN}${url}`;
}

function upsertMeta(attr: 'name' | 'property', key: string, value: string): void {
  let node = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!node) {
    node = document.createElement('meta');
    node.setAttribute(attr, key);
    document.head.appendChild(node);
  }
  node.setAttribute('content', value);
}

/** Set every SEO-relevant head tag for the current page. Idempotent. */
export function applyMeta({
  title,
  description,
  ogImage = '/og-image.png',
  path,
  noindex,
}: MetaInput): void {
  const fullTitle = title.includes('Orthodox Way') ? title : `${title}${TITLE_SUFFIX}`;
  const canonical = absolute(path ?? window.location.pathname);
  const image = absolute(ogImage);

  document.title = fullTitle;
  upsertMeta('name', 'description', description);
  upsertMeta('property', 'og:title', fullTitle);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:image', image);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', fullTitle);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', image);

  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', canonical);

  const robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (noindex) {
    upsertMeta('name', 'robots', 'noindex, nofollow');
  } else if (robots) {
    robots.remove();
  }
}

/**
 * Page-level meta hook. Runs after the route-level defaults (RouteSeo is an
 * earlier sibling in the tree, so its effect fires first on navigation), which
 * lets detail pages override with fetched data, e.g. a saint's name, once
 * it arrives.
 */
export function useMeta(input: MetaInput): void {
  useEffect(() => {
    applyMeta(input);
    // Values are primitives; spreading them keeps the effect honest without
    // re-running on referentially-new but equal objects.
  }, [input.title, input.description, input.ogImage, input.path, input.noindex]);
}
